// Core
import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'

// Library
import clsx from 'clsx'

// Components
import StepProgress from 'components/Onboarding/StepProgress/StepProgress'
import BlockQuote from 'components/Onboarding/BlockQuote/BlockQuote'
import BulletList from 'components/Onboarding/BulletList/BulletList'
import Button from 'components/Onboarding/Button/Button'

// Assets
import { ReactComponent as Logo } from 'assets/icons/logo_clicktech.svg'
import { ReactComponent as IconDD } from 'assets/icons/logo_dd.svg'
import { ReactComponent as ReportingIcon } from 'assets/icons/onboarding/report.svg'
import { ReactComponent as AutomationIcon } from 'assets/icons/onboarding/automation.svg'
import { ReactComponent as OverspendIcon } from 'assets/icons/onboarding/overspend.svg'
import { ReactComponent as PerformanceIcon } from 'assets/icons/onboarding/performance.svg'

// Styles
import classes from 'pages/Onboarding/Onboarding.module.scss'
import ButtonOptions from 'components/Onboarding/ButtonOptions/ButtonOptions'

// Constants
import { PRIORITY_VALUES } from 'pages/Onboarding/Profile/Priorities/constants'

// Routes
import routes from 'routes'

// Utils
import {sendGTMEvent} from 'utils/gtm/sendGTMEvent'

const ProfilePriorities = () => {
  const history = useHistory()
  const [selectedOptions, setSelectedOptions] = useState([] as string[])
  const options = [
    {
      value: PRIORITY_VALUES.REPORTING,
      icon: <ReportingIcon />,
      title: 'Budget Pacing Reporting',
      description: 'Automated Budget Pacing Reporting that Saves You Time',
    },
    {
      value: PRIORITY_VALUES.AUTOMATION,
      icon: <AutomationIcon />,
      title: 'Budget Automation',
      description: 'Automate Budget Management for Optimal Performance',
    },
    {
      value: PRIORITY_VALUES.OVERSPEND,
      icon: <OverspendIcon />,
      title: 'Stop Overspends',
      description: 'Prevent Campaign Overspend with Real-time Alerts',
    },
    {
      value: PRIORITY_VALUES.PERFORMANCE,
      icon: <PerformanceIcon />,
      title: 'Track Performance',
      description: 'Monitor Campaign Metrics and Detailed Performance Reports',
    }
  ]

  const handleContinue = () => {
    const onboardingInfoString = localStorage.getItem('onboarding')
    const onboardingInfo = onboardingInfoString != null ? JSON.parse(onboardingInfoString) : {}
    onboardingInfo['priorities'] = selectedOptions
    localStorage.setItem('onboarding', JSON.stringify(onboardingInfo))
    localStorage.setItem('onboarding_pf', JSON.stringify(onboardingInfo))

    sendGTMEvent({
      event: 'priorityComplete',
      priority: selectedOptions.join(',')
    })

    history.push(routes.onboardingCompanyDetails)
  }
  
  return (
    <div className={clsx(classes.stepContainer)}>
      <div className={clsx(classes.stepAction)}>
        <div className={clsx(classes.stepSection, 'mt-2', 'mt-xl-up-5')}>
          <div className={clsx(classes.logo)}>
            <Logo />
          </div>
        </div>
        <div className={clsx(classes.stepSection)}>
          <div className={clsx(classes.stepProgress)}>
            <StepProgress step={1} label="account" theme="complete" />
            <StepProgress step={2} label="profile" theme="active" />
            <StepProgress step={3} label="usage" theme="pending" className="d-none-sm" />
            <StepProgress step={4} label="connect" theme="pending" className="d-none-sm" />
          </div>
        </div>
        <div className={clsx(classes.stepSection, 'mt-2')}>
          <h1>Tell us about your priorities</h1>
        </div>
        <div className={clsx(classes.stepSection, 'mt-2 mb-4')}>
          <p>This helps us tailor EDEE to your specific needs.</p>
        </div>
        <div className={clsx(classes.stepSection, 'mt-2 mb-4')}>
          <ButtonOptions
            title={<p className="mb-3"><strong>What are your key priorities?</strong> (select one or more)</p>}
            options={options}
            selectedOptions={selectedOptions}
            onClick={(value: string) => {
                const existingIndex = selectedOptions.indexOf(value)
                const newSelectedOptions = [ ...selectedOptions ]
                if (existingIndex > -1) {
                  newSelectedOptions.splice(existingIndex, 1)
                } else {
                  newSelectedOptions.push(value)
                }
                setSelectedOptions(newSelectedOptions)
            }}
          />
        </div>
        <div className={clsx(classes.stepSection, 'mt-2 mb-4')}>
          <div className="d-flex justify-content-end">
            <Button
              inverted
              className="mr-1"
              dataCy="back"
              title="Back"
              onClick={() => {
                history.push(routes.onboardingProfileBusiness)
              }}
            />
            <Button
              dataCy="continue"
              title="Continue"
              disabled={selectedOptions.length === 0}
              onClick={handleContinue}
            />
          </div>
        </div>
      </div>
      <div className={clsx(classes.infoContainer)}>
        <div className={clsx(classes.infoSection)}>
          <h2 className="fw-semibold">
            EDEE works the way you need it to
          </h2>
        </div>
        <div className={clsx(classes.infoSection, 'mt-2')}>
          <BulletList
            features={[
              'Create custom budget cycles',
              'One-off or repeating budget cycles',
              'Set up multiple budgets per ad account',
              'Track rollover amounts',
              '& more!',
            ]} 
          />
        </div>
        <div className={clsx(classes.infoSection, 'mt-8')}>
          <BlockQuote
            srcImage={<IconDD />}
            quote="EDEE is by far the best tool I've used for budget management. One-stop budget pacing is quick and easy, and I love the features to avoid overspend. Checking budgets for all my accounts now takes maybe 5 minutes, and the automated system to pause campaigns if we reach budgets early lets me sleep better at night."
            author="Taru K, PPC Specialist"
          />
        </div>
      </div>
    </div>
  )
}

export default ProfilePriorities
